import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import type { Evento, Invitacion, Mesa, MiembroEvento, RolEvento } from '@eventos-cien/shared';
import { useAuth } from '../auth/AuthProvider';
import { db } from '../lib/firebase';

export interface MesaConId extends Mesa {
  id: string;
}

interface PanelData {
  cargando: boolean;
  /** true mientras la ultima snapshot vino solo de la cache local (sin red). */
  sinConexion: boolean;
  eventoId: string;
  misRoles: RolEvento[];
  evento: Evento | null;
  invitaciones: Invitacion[];
  mesas: MesaConId[];
  /** Propio doc eventos/{eventoId}/miembros/{uid}; solo se escucha para asesores. */
  miembro: MiembroEvento | null;
  /** Si la lectura en vivo de invitaciones falla (p. ej. permisos), un aviso para mostrar en vez de fallar en silencio con una lista vacía. */
  errorInvitaciones: string | null;
}

const Ctx = createContext<PanelData | null>(null);

/**
 * Reemplaza a puerta/PuertaData.tsx: acotado a un eventoId, y la query de
 * invitaciones depende del rol real en ESTE evento — un asesor sin rol de
 * staff solo puede leer las suyas (asesorUid == uid, exigido por las
 * reglas de seguridad), mientras que staff/admin leen todas. Igual que la
 * puerta original, usa la cache persistente de Firestore para funcionar
 * sin señal.
 */
export function PanelDataProvider({
  eventoId,
  misRoles,
  children,
}: {
  eventoId: string;
  misRoles: RolEvento[];
  children: ReactNode;
}) {
  const { user } = useAuth();
  const [evento, setEvento] = useState<Evento | null>(null);
  const [invitaciones, setInvitaciones] = useState<Invitacion[] | null>(null);
  const [mesas, setMesas] = useState<MesaConId[]>([]);
  const [miembro, setMiembro] = useState<MiembroEvento | null>(null);
  const [sinConexion, setSinConexion] = useState(false);
  const [errorInvitaciones, setErrorInvitaciones] = useState<string | null>(null);

  const esStaff = misRoles.includes('staff');
  const esAsesor = misRoles.includes('asesor');

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      doc(db, 'eventos', eventoId),
      (s) => setEvento(s.exists() ? (s.data() as Evento) : null),
      () => setEvento(null),
    );
  }, [eventoId, user]);

  useEffect(() => {
    if (!user) return;
    setInvitaciones(null);
    setErrorInvitaciones(null);
    const base = collection(db, 'eventos', eventoId, 'invitaciones');
    const q = esStaff ? base : query(base, where('asesorUid', '==', user.uid));
    return onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        setInvitaciones(snap.docs.map((d) => d.data() as Invitacion));
        setSinConexion(snap.metadata.fromCache);
      },
      (err) => {
        // Antes esto dejaba `invitaciones` en [] sin avisar: buscar/escanear
        // se veían "normales" pero nunca encontraban a nadie, sin pista de
        // que la lectura había fallado (p. ej. permisos con un código de
        // acceso desactivado). Mejor mostrar el error explícito.
        console.error('Error al leer invitaciones en vivo:', err);
        setInvitaciones([]);
        setErrorInvitaciones('No se pudo cargar la lista de invitados. Verifica tu conexión o pide que revisen tu acceso.');
      },
    );
  }, [eventoId, esStaff, user]);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(
      collection(db, 'eventos', eventoId, 'mesas'),
      (s) => setMesas(s.docs.map((d) => ({ ...(d.data() as Mesa), id: d.id }))),
      () => setMesas([]),
    );
  }, [eventoId, user]);

  useEffect(() => {
    if (!user || !esAsesor) {
      setMiembro(null);
      return;
    }
    return onSnapshot(doc(db, 'eventos', eventoId, 'miembros', user.uid), (s) =>
      setMiembro(s.exists() ? (s.data() as MiembroEvento) : null),
    );
  }, [eventoId, esAsesor, user]);

  const value: PanelData = {
    cargando: invitaciones === null,
    sinConexion,
    eventoId,
    misRoles,
    evento,
    invitaciones: invitaciones ?? [],
    mesas,
    miembro,
    errorInvitaciones,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePanelData(): PanelData {
  const c = useContext(Ctx);
  if (!c) throw new Error('usePanelData debe usarse dentro de <PanelDataProvider>');
  return c;
}
